import React from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/shared/LocalizedLink";
import { SubscribeDialog } from "@/components/billing/SubscribeDialog";
import { DemoPortfolioButton } from "@/components/shared/DemoPortfolioButton";
import { T, useT, type TranslationKey } from "@/lib/translation";

/**
 * "signup" gates an anonymous visitor on the public research pages and sends them to signup.
 * "upgrade" gates a signed-in free-tier user and sends them to the pricing page. Both blur the
 * content and overlay a CTA; only the call to action differs.
 */
type GateVariant = "signup" | "upgrade";

interface GatedContainerProps {
  children: React.ReactNode;
  /** Gate is open (children render raw) when true. */
  isLoggedIn: boolean;
  /** Required for the signup variant, which redirects back to the ticker after signup. */
  ticker?: string;
  variant?: GateVariant;
  title?: string;
  description?: string;
  /**
   * Drops the card frame (border/background/rounded corners/padding) around the blurred
   * content. Use this when the caller is already inside its own card (so a second frame
   * would nest boxes) or is blurring a whole page (so a frame would look like a floating
   * white window instead of the page itself).
   */
  frameless?: boolean;
  /**
   * Upgrade variant only: also offers to try the feature on the demo portfolio, staying on
   * the page. Only for features that run on a portfolio, since the demo unlocks those.
   */
  showDemoOption?: boolean;
}

const DEFAULT_COPY: Record<
  GateVariant,
  { title?: TranslationKey; description: TranslationKey }
> = {
  signup: {
    title: "common:gatedContainer.signupTitle",
    description: "common:gatedContainer.signupDescription",
  },
  upgrade: {
    description: "common:gatedContainer.upgradeDescription",
  },
};

export function GatedContainer({
  children,
  isLoggedIn,
  ticker,
  variant = "signup",
  title,
  description,
  frameless = false,
  showDemoOption = false,
}: GatedContainerProps) {
  const t = useT();

  if (isLoggedIn) {
    return <>{children}</>;
  }

  const copy = DEFAULT_COPY[variant];
  const redirectTarget = ticker
    ? `/research/${ticker.toUpperCase()}`
    : "/research";

  return (
    <div
      className={
        frameless
          ? "relative overflow-hidden min-h-[50vh]"
          : "relative overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-sm"
      }
    >
      {/* 1. Visually blurred content: Googlebot can still index the text/headings inside it! */}
      <div
        className={`filter blur-md opacity-25 select-none pointer-events-none ${frameless ? "" : "p-6"}`}
        aria-hidden="true"
      >
        {children}
      </div>

      {/* 2. CTA card, matching the rest of the site's styling.
          Frameless callers blur a whole page/section whose content is still loading (a
          skeleton, then a real or error state once the request settles), so its height
          changes after mount. Centering within `inset-0` would recompute the center each
          time and visibly shift the card. Anchoring to a fixed offset from the top instead
          keeps it still regardless of how the blurred content's height changes below it. */}
      <div
        className={`absolute inset-0 flex flex-col items-center p-6 bg-background/50 backdrop-blur-[2px] text-center z-20 ${
          frameless ? "justify-start pt-16 sm:pt-24" : "justify-center"
        }`}
      >
        <div className="max-w-sm space-y-8 bg-card border p-8 rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="space-y-2">
            {(title ?? copy.title) && (
              <h3 className="text-xl font-bold tracking-tight text-foreground">
                {title ?? (copy.title && t(copy.title))}
              </h3>
            )}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {description ?? t(copy.description)}
            </p>
          </div>

          {variant === "upgrade" ? (
            <div className="space-y-3">
              <SubscribeDialog>
                <Button className="w-full rounded-2xl py-6 text-base">
                  {t("common:gatedContainer.subscribeToAccess")}
                </Button>
              </SubscribeDialog>
              {showDemoOption && (
                <DemoPortfolioButton
                  label={t("common:gatedContainer.tryDemo")}
                  stayOnPage
                  variant="outline"
                  className="w-full rounded-2xl py-6 text-base"
                />
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <Link
                to={`/signup?redirect=${encodeURIComponent(redirectTarget)}`}
                className="block"
              >
                <Button className="w-full rounded-2xl py-6 text-base">
                  {t("common:gatedContainer.getEarlyAccess")}
                </Button>
              </Link>
              <p className="text-xs text-muted-foreground">
                <T
                  k="common:gatedContainer.haveAccount"
                  components={{
                    signin: (
                      <Link
                        to={`/login?redirect=${encodeURIComponent(redirectTarget)}`}
                        className="font-medium text-primary underline hover:text-primary/80"
                      />
                    ),
                  }}
                />
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
